import React, { useCallback, useEffect, useState, useMemo } from "react";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import { addMonths, format, subMonths } from "date-fns";
import { ko } from "date-fns/locale";
import {
  useBusinessCalendar,
  type BusinessCalendarTicketItem,
} from "~/services/business/main";

interface CalendarMainProps {
  sharedDate: {
    year: number;
    month: number;
    day: number;
  };
  setSharedDate: React.Dispatch<
    React.SetStateAction<{
      year: number;
      month: number;
      day: number;
    }>
  >;
}

const CalendarMain = ({ sharedDate, setSharedDate }: CalendarMainProps) => {
  // sharedDate를 기반으로 selectedDay 계산
  const selectedDay = useMemo(() => {
    return new Date(sharedDate.year, sharedDate.month, sharedDate.day);
  }, [sharedDate]);
  const [ticketListData, setTicketListData] = useState<
    BusinessCalendarTicketItem[]
  >([]);

  // 선택된 날짜에서 yearMonthDay 생성
  const yearMonthDay = selectedDay
    ? `${selectedDay.getFullYear()}${(selectedDay.getMonth() + 1).toString().padStart(2, "0")}${selectedDay.getDate().toString().padStart(2, "0")}`
    : "";

  // 캘린더 API 호출 - 선택된 날짜로 호출
  const { data: calendarData } = useBusinessCalendar({
    yearMonthDay,
  });
  // API 데이터에서 summaries / tickets 추출
  useEffect(() => {
    if (!calendarData?.props?.length) {
      setTicketListData([]);
      return;
    }
    const ticketsProp = calendarData.props.find((p) => p.type === "tickets");

    if (ticketsProp?.type === "tickets" && Array.isArray(ticketsProp.datas)) {
      setTicketListData(ticketsProp.datas as BusinessCalendarTicketItem[]);
    } else {
      setTicketListData([]);
    }
  }, [calendarData]);

  const selectedDateLabel = useMemo(
    () => format(selectedDay, "yyyy년 M월 d일 (EEE)", { locale: ko }),
    [selectedDay],
  );

  const handleSelectDay = (day: Date | null) => {
    if (day) {
      // 공유 날짜 상태만 업데이트
      setSharedDate({
        year: day.getFullYear(),
        month: day.getMonth(),
        day: day.getDate(),
      });
    }
  };

  const handleSelectDate = useCallback(
    (isNext: boolean) => {
      // 함수형 업데이터로 최신 sharedDate 기준 ±1개월 계산
      // (CalendarNew의 handlePrev/Next가 빈 의존성 useCallback이라
      //  selectedDay 참조 시 stale closure로 항상 초기값 기준으로 계산되는 문제 회피)
      setSharedDate((prev) => {
        const currentDate = new Date(prev.year, prev.month, prev.day);
        const newDate = isNext
          ? addMonths(currentDate, 1)
          : subMonths(currentDate, 1);
        return {
          year: newDate.getFullYear(),
          month: newDate.getMonth(),
          day: 1,
        };
      });
    },
    [setSharedDate],
  );
  return (
    <>
      <CalendarComponent
        disabled={false}
        dayOnClick={(day) => {
          handleSelectDay(day);
        }}
        handleSelectDate={handleSelectDate}
        selectedDay={selectedDay}
        schedules={
          (calendarData?.calendar as { [key: string]: string[] }) || {}
        }
      />
      <div className="flex flex-col bg-[#F5F5F5]">
        <div className="grid grid-cols-1 gap-1 px-4">
          {/* 해당일 사용된 이용권 + 고객 정보 (간단 리스트) — API props.type === "tickets" */}
          <div className="pb-6 mt-4">
            <div className="flex justify-between items-baseline px-1 mb-2">
              <div className="flex gap-2 items-baseline">
                <div className="text-[14px] font-semibold text-[#111111]">
                  예약 내역
                </div>
                <span className="text-sm text-gray-700">
                  총 <span className="font-semibold text-gray-900">{ticketListData.length}</span>건
                </span>
              </div>
              <div className="text-[12px] text-[#757575]">
                {selectedDateLabel}
              </div>
            </div>

            {/* 컬럼 헤더: 보호자 / 반려견 / 이용권 / 잔여 */}
            <div className="grid grid-cols-12 gap-1 items-center px-2 mb-2">
              <div className="col-span-3 text-[13px] text-[#666666] text-center">
                보호자
              </div>
              <div className="col-span-3 text-[13px] text-[#666666] text-center">
                반려견
              </div>
              <div className="col-span-4 text-[13px] text-[#666666] text-center">
                이용권
              </div>
              <div className="col-span-2 text-[13px] text-[#666666] text-center">
                잔여
              </div>
            </div>

            <div className="min-h-[120px]">
              {ticketListData.length === 0 ? (
                <div className="flex justify-center items-center py-12 text-[#666666] bg-white rounded-lg border border-[#e9e9e9]">
                  예약 내역이 없습니다.
                </div>
              ) : (
                ticketListData.map((row, index) => {
                  const isHotel = row.cdProductType === "PRODUCT_TYPE_HOTEL";
                  const hasQty =
                    !isHotel &&
                    typeof row.remainQty === "number" &&
                    typeof row.totalQty === "number";
                  return (
                    <div
                      key={`${row.userName}-${row.petName}-${row.ticketName}-${index}`}
                      className="mb-2"
                    >
                      <div className="grid grid-cols-12 gap-1 items-center w-full bg-white rounded-lg border border-[#e9e9e9] min-h-[50px] px-2 py-3 text-left">
                        <div className="col-span-3 min-w-0 text-[13px] text-[#333] text-center truncate">
                          {row.userName ?? "-"}
                        </div>
                        <div className="col-span-3 min-w-0 text-[13px] text-[#333] text-center truncate">
                          {row.petName ?? "-"}
                        </div>
                        <div className="col-span-4 min-w-0 text-[13px] text-[#333] text-center truncate">
                          {row.ticketName ?? "-"}
                        </div>
                        <div className="col-span-2 min-w-0 text-[12px] leading-tight text-center">
                          {hasQty ? (
                            <div className="flex flex-col">
                              <span className="text-[#333]">
                                {(row.totalQty ?? 0) - (row.remainQty ?? 0)}회 사용
                              </span>
                              <span
                                className={
                                  row.remainQty === 0
                                    ? "text-[#BDBDBD]"
                                    : "text-[#FF9934] font-semibold"
                                }
                              >
                                ({row.remainQty}회 남음)
                              </span>
                            </div>
                          ) : (
                            <span className="text-[#BDBDBD]">-</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CalendarMain;
